import Combobox from './combobox';

/**
 * Custom select dropdown that provides autocomplete functionality.
 */
class AutocompleteCombobox extends Combobox {

  _lastSearch = null;
  _ignoreNextSearch = false;

  /**
   * Create new dropdown combobox.
   *
   * @param {ComboboxOptions} options Dropdown options.
   */
  constructor(options = {}) {
    if (!options.allowEmpty && options.select && !options.placeholder) {
      const emptyLabel = Array.from(options.select.options).find(item => item.value === '')?.innerText;
      if (emptyLabel) {
        options.placeholder = emptyLabel;
      }
    }
    options = {
      ...(options || {})
    };
    super(options);
  }

  /**
   * @inheritdoc
   */
  createElements() {
    const elements = super.createElements();
    const config = this.getConfig();

    const container = document.createElement('div');
    container.id = this.getId();
    container.classList.add(config.cssContainerClass);

    const input = document.createElement('input');
    input.type = 'text';
    input.id = this.getId() + '-control';
    input.classList.add(config.cssControlClass);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', config.browserAutocomplete || 'on');
    input.placeholder = config.placeholder;
    input.disabled = config.disabled;

    const inputContainer = document.createElement('div');
    inputContainer.classList.add(config.cssClassPrefix + '__control-wrapper');

    const button = document.createElement('div');
    button.classList.add(config.cssClassPrefix + '__control-button');

    inputContainer.appendChild(input);
    inputContainer.appendChild(button);

    container.appendChild(inputContainer);
    container.appendChild(elements.list);

    return {
      ...elements,
      container,
      control: input
    };
  }

  /**
   * @inheritdoc
   * @protected
   */
  updateSelection(content, data) {
    // Do not render label for empty value since it is already used as text field placeholder
    if (data.value.length === 1 && !data.value[0]) {
      content = '';
    }

    this.getElements().control.value = typeof this._lastSearch === 'string' ? this._lastSearch : (content || '');
  }

  /**
   * Changes active item based on keypress.
   *
   * @param {string} key Pressed key.
   */
  #handleActiveItemChange(key) {
    const options = this.getSelectableItems();
    if (options.length === 0) {
      return;
    }

    const positions = {
      'ArrowUp': -1,
      'ArrowDown': 1,
    };
    const position = positions[key];

    const option = this.findSelectableItem(this.getActiveItem() || options[0], position, true);
    if (option) {
      this.setActiveItem(option, true, position < 0 ? 'top' : 'bottom');
    }
  }

  /**
   * @inheritdoc
   * @protected
   */
  onKeyUp(e) {
    if (this._ignoreNextSearch) {
      this._ignoreNextSearch = false;
      return;
    }

    if (e.target.value !== this._lastSearch) {
      this._lastSearch = e.target.value;
      this.search(e.target.value);

      if (e.target.value) {
        this.open(true);
      }
    }
  }

  /**
   * Handles key events.
   *
   * @param {KeyboardEvent} e Triggered event.
   * @protected
   */
  onKeyDown(e) {
    const map = {
      'Esc': 'Escape'
    };
    const actionKey = map[e.key] || e.key;

    if (!this.isOpen()) {
      switch(actionKey) {
      case 'Enter':
      case 'ArrowDown': {
        e.preventDefault();
        this._ignoreNextSearch = true;
        this.open(true);
        return;
      }
      case 'Escape': {
        e.preventDefault();
        this._ignoreNextSearch = true;
        this._lastSearch = '';
        this.setFilteredItems(null);
        return;
      }
      case 'ArrowUp': {
        e.preventDefault();
        this._ignoreNextSearch = true;
        this.open(true);
        this.#handleActiveItemChange(actionKey);
        return;
      }
      }
    }
    else {
      switch(actionKey) {
      case 'Escape': {
        e.preventDefault();
        this._ignoreNextSearch = true;
        this._lastSearch = null;
        this.setFilteredItems(null);
        this.renderSelection();
        this.close(true);
        return;
      }
      case 'Enter': {
        e.preventDefault();

        this._ignoreNextSearch = true;
        this._lastSearch = null;

        const activeOption = this.getActiveItem();
        if (activeOption) {
          this.toggleValue(activeOption.dataset.value);

          if (!this.isMultiSelect()) {
            this.close(true);
          }
        }

        this.setFilteredItems(null);
        return;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        this._ignoreNextSearch = true;
        this.#handleActiveItemChange(actionKey);
        return;
      }
      }
    }
  }

  /**
   * @inheritdoc
   */
  addEventListeners() {
    super.addEventListeners();

    // Clear search state when option is selected via click
    this.getElements().list.addEventListener('click', (e) => {
      const option = e.target.closest('[role="option"]');
      if (option && this.isItemSelectable(option)) {
        this._lastSearch = null;
        this.setFilteredItems(null);
      }
    });
  }

  /**
   * @inheritdoc
   */
  onControlBlur(e) {
    super.onControlBlur(e);
    if (!this.getElements().container.contains(e.relatedTarget)) {
      this._lastSearch = null;
      this.setFilteredItems(null);
    }
  }

  /**
   * Search options which text includes given search phrase. Activates first match.
   *
   * @param {string} search Search phrase.
   */
  search(search) {
    const content = this.getDataSource().getContent().map(item => item.items ? item.items : item).flat();
    let filterCallback = this.getConfig()?.onSearch?.call(this, search, content) || null;
    if (typeof filterCallback !== 'function') {
      filterCallback = (item) => {
        return !search || item.text.toLowerCase().indexOf(search.toLowerCase()) > -1;
      };
    }

    const result = content.filter(filterCallback).map(item => item.value);
    this.setFilteredItems(result);
    const item = search ? this.getSelectableItems().find((item, i) => i === 0) : this.findSelectableItem(null, 0);
    if (item) {
      this.setActiveItem(item);
    }
    console.log(search, result);
  }

}

export default AutocompleteCombobox;
